import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import Confetti from "react-confetti";
import { Trophy, Star, Sparkles, Gift } from "lucide-react";
import { useWindowSize } from "@/hooks/use-window-size";
import { Link } from "react-router-dom";
import { LEVEL_UNLOCKS } from "@/constants/shopItems";

interface LevelUpModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  level: number;
  totalPoints: number;
}

export function LevelUpModal({
  open,
  onOpenChange,
  level,
  totalPoints,
}: LevelUpModalProps) {
  const { width, height } = useWindowSize();
  const unlocks = LEVEL_UNLOCKS[level] || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        {open && (
          <Confetti
            width={width}
            height={height}
            recycle={false}
            numberOfPieces={200}
            gravity={0.3}
          />
        )}
        
        <div className="flex flex-col items-center justify-center space-y-6 py-8">
          <div className="relative">
            <div className="absolute inset-0 animate-ping">
              <Trophy className="h-24 w-24 text-yellow-500 opacity-75" />
            </div>
            <Trophy className="h-24 w-24 text-yellow-500 relative z-10" />
          </div>

          <div className="text-center space-y-2">
            <h2 className="text-3xl font-bold bg-gradient-to-r from-yellow-500 to-orange-500 bg-clip-text text-transparent">
              Level Up!
            </h2>
            <p className="text-5xl font-bold text-primary">
              Level {level}
            </p>
          </div>

          <div className="flex items-center gap-2 text-muted-foreground">
            <Star className="h-5 w-5 text-yellow-500" />
            <span className="text-lg">{totalPoints} Total Points</span>
          </div>

          {/* Unlocked items */}
          {unlocks.length > 0 && (
            <div className="w-full space-y-2">
              <p className="text-sm font-medium text-center flex items-center justify-center gap-2">
                <Gift className="h-4 w-4 text-primary" />
                New Unlocks:
              </p>
              <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-lg p-3 space-y-1">
                {unlocks.map((unlock) => (
                  <p key={unlock} className="text-sm text-center">
                    ✨ {unlock}
                  </p>
                ))}
              </div>
            </div>
          )}

          <div className="w-full space-y-2">
            <p className="text-center text-sm text-muted-foreground">
              Keep up the great work! 🎉
            </p>
            <div className="flex gap-2">
              <Button
                onClick={() => onOpenChange(false)}
                variant="outline"
                className="flex-1"
              >
                Continue
              </Button>
              <Link to="/my-progress?tab=customize" className="flex-1">
                <Button
                  onClick={() => onOpenChange(false)}
                  className="w-full gap-2"
                >
                  <Sparkles className="h-4 w-4" />
                  Unlock Items
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
